import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  parkingManagementApi,
  type AccessCard,
  type AccessCardKind,
  type AccessCardPickerPurpose,
} from '../../api/parkingManagementApi';

function formatCardLabel(card: AccessCard): string {
  return card.cardCode ? `${card.cardNumber} (${card.cardCode})` : card.cardNumber;
}

type AccessCardPickerOption = Pick<
  AccessCard,
  'id' | 'cardNumber' | 'cardCode' | 'vehicleCardTypeId' | 'vehicleCardType'
>;

interface AccessCardSearchPickerProps {
  buildingId: string;
  kind: AccessCardKind;
  purpose?: AccessCardPickerPurpose;
  excludeMonthlyVehicleId?: string;
  value: string;
  /** Shown when `value` is the vehicle's current (possibly invoiced) card — not in "available" search. */
  currentCard?: AccessCardPickerOption | null;
  onChange: (cardId: string, card: AccessCard | null) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  hideHint?: boolean;
}

export const AccessCardSearchPicker: React.FC<AccessCardSearchPickerProps> = ({
  buildingId,
  kind,
  purpose = 'STOCK',
  excludeMonthlyVehicleId,
  value,
  currentCard = null,
  onChange,
  disabled = false,
  label = 'Chọn thẻ (số thẻ hoặc mã UID)',
  placeholder = 'Gõ số thẻ hoặc mã UID (tối thiểu 2 ký tự)...',
  hideHint = false,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AccessCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<AccessCard | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery('');
    setResults([]);
    setSelectedCard(null);
    setOpen(false);
  }, [buildingId, kind]);

  useEffect(() => {
    if (!value) {
      setSelectedCard(null);
      if (!open) setQuery('');
      return;
    }
    if (currentCard?.id === value) {
      const card = currentCard as AccessCard;
      setSelectedCard(card);
      setQuery(formatCardLabel(card));
    }
  }, [value, currentCard, open]);

  const searchCards = useCallback(
    async (term: string) => {
      if (!buildingId || term.trim().length < 2) {
        setResults([]);
        return;
      }
      try {
        setLoading(true);
        const cards = await parkingManagementApi.listAvailableAccessCards({
          buildingId,
          kind,
          search: term.trim(),
          limit: 20,
          purpose,
          excludeMonthlyVehicleId,
        });
        setResults(cards);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [buildingId, kind, purpose, excludeMonthlyVehicleId],
  );

  useEffect(() => {
    if (!open || selectedCard) return;
    const timer = setTimeout(() => {
      searchCards(query);
    }, 250);
    return () => clearTimeout(timer);
  }, [query, open, selectedCard, searchCards]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (card: AccessCard) => {
    setSelectedCard(card);
    setQuery(formatCardLabel(card));
    setOpen(false);
    setResults([]);
    onChange(card.id, card);
  };

  const handleClear = () => {
    setSelectedCard(null);
    setQuery('');
    setResults([]);
    setOpen(true);
    onChange('', null);
  };

  const handleInputChange = (next: string) => {
    if (selectedCard) {
      setSelectedCard(null);
      onChange('', null);
    }
    setQuery(next);
    setOpen(true);
  };

  const showDropdown = open && !disabled && !selectedCard;
  const trimmed = query.trim();

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className="w-full px-3 py-2 pr-9 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {selectedCard && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            title="Xóa lựa chọn"
          >
            ×
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {loading ? (
            <div className="px-3 py-2 text-sm text-gray-500">Đang tìm...</div>
          ) : trimmed.length < 2 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              Nhập ít nhất 2 ký tự để tìm thẻ
            </div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">Không tìm thấy thẻ khả dụng</div>
          ) : (
            results.map((card) => (
              <button
                key={card.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(card)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
              >
                <div className="font-medium text-gray-900">{card.cardNumber}</div>
                {card.cardCode && (
                  <div className="text-xs text-gray-500">UID: {card.cardCode}</div>
                )}
              </button>
            ))
          )}
        </div>
      )}

      {!hideHint && (
        <p className="mt-1 text-xs text-gray-500">
          Mỗi hóa đơn gắn với một thẻ. Số lượng luôn là 1.
        </p>
      )}
    </div>
  );
};
